<script setup>
import { computed, ref, onMounted, onBeforeUnmount, useTemplateRef } from "vue";
import { cn } from "@/shared/lib/utils";
import { actionBarPositionerVariants } from ".";
import { useActionBar } from "./context";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  class: { type: [String, Object, Array], default: undefined },
  positionerClass: { type: [String, Object, Array], default: undefined },
  draggable: { type: Boolean, default: false },
  storageKey: { type: String, default: "" },
});

const { isOpen, positioning } = useActionBar();

const placement = computed(() => positioning.value.placement);
const positionerStyle = computed(() => ({
  "--gutter": positioning.value.gutter,
}));

const contentRef = useTemplateRef("contentRef");
const offset = ref({ x: 0, y: 0 });
const dragging = ref(false);
let start = null;

onMounted(() => {
  if (!props.draggable || !props.storageKey) return;
  try {
    const saved = JSON.parse(localStorage.getItem(props.storageKey) || "null");
    if (saved && typeof saved.x === "number" && typeof saved.y === "number") {
      offset.value = saved;
    }
  } catch {
    /* ignore */
  }
});

const contentStyle = computed(() =>
  props.draggable
    ? {
        transform: `translate(${offset.value.x}px, ${offset.value.y}px)`,
        touchAction: "none",
      }
    : {}
);

function clamp(nx, ny) {
  const el = contentRef.value;
  if (!el) return { x: nx, y: ny };
  const rect = el.getBoundingClientRect();
  const baseLeft = rect.left - offset.value.x;
  const baseTop = rect.top - offset.value.y;
  const minX = -baseLeft + 8;
  const maxX = window.innerWidth - rect.width - baseLeft - 8;
  const minY = -baseTop + 8;
  const maxY = window.innerHeight - rect.height - baseTop - 8;
  return {
    x: Math.min(Math.max(nx, minX), maxX),
    y: Math.min(Math.max(ny, minY), maxY),
  };
}

function onPointerMove(event) {
  if (!dragging.value || !start) return;
  offset.value = clamp(
    start.ox + (event.clientX - start.px),
    start.oy + (event.clientY - start.py)
  );
}

function onPointerUp() {
  dragging.value = false;
  start = null;
  document.body.style.userSelect = "";
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", onPointerUp);
  if (props.draggable && props.storageKey) {
    try {
      localStorage.setItem(props.storageKey, JSON.stringify(offset.value));
    } catch {
      /* ignore */
    }
  }
}

function onHandleDown(event) {
  if (!props.draggable) return;
  event.preventDefault();
  dragging.value = true;
  document.body.style.userSelect = "none";
  start = {
    px: event.clientX,
    py: event.clientY,
    ox: offset.value.x,
    oy: offset.value.y,
  };
  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
}

onBeforeUnmount(() => {
  document.body.style.userSelect = "";
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", onPointerUp);
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="animate-in fade-in-0 slide-in-from-bottom-2 duration-200"
      leave-active-class="animate-out fade-out-0 slide-out-to-bottom-2 duration-200"
    >
      <div
        v-if="isOpen"
        data-slot="action-bar-positioner"
        :data-placement="placement"
        :style="positionerStyle"
        :class="
          cn(actionBarPositionerVariants({ placement }), props.positionerClass)
        "
      >
        <div
          ref="contentRef"
          data-slot="action-bar-content"
          role="toolbar"
          v-bind="$attrs"
          :style="contentStyle"
          :class="
            cn(
              'pointer-events-auto flex w-fit items-center gap-1 rounded-xl border bg-popover text-popover-foreground shadow-lg px-4 py-2',
              props.class
            )
          "
        >
          <div
            v-if="draggable"
            class="flex size-8 shrink-0 select-none items-center justify-center rounded-[var(--radius)] border border-border text-muted-foreground"
            :style="{ cursor: dragging ? 'grabbing' : 'grab' }"
            @pointerdown="onHandleDown"
          >
            <svg
              width="10"
              height="16"
              viewBox="0 0 10 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <circle cx="2.5" cy="3" r="1.3" />
              <circle cx="7.5" cy="3" r="1.3" />
              <circle cx="2.5" cy="8" r="1.3" />
              <circle cx="7.5" cy="8" r="1.3" />
              <circle cx="2.5" cy="13" r="1.3" />
              <circle cx="7.5" cy="13" r="1.3" />
            </svg>
          </div>
          <slot />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
